import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, styled, CircularProgress, Typography } from '@mui/material';
import { MindmapNode, MindmapResponse } from '@services/mindmap';

const MindmapContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '500px',
  border: 'none',
  borderRadius: '16px',
  background: '#EEFCFF',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: 'none',
  [theme.breakpoints.down('md')]: {
    height: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '400px',
  },
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '99%',
  flexDirection: 'column',
  gap: 2,
  color: '#6b7280',
});

const TooltipContainer = styled(Box)({
  position: 'absolute',
  background: '#ffffff',
  color: '#262626',
  padding: '8px',
  borderRadius: '8px',
  fontSize: '14px',
  pointerEvents: 'auto',
  zIndex: 1000,
  maxWidth: '320px',
  minWidth: '280px',
  border: '1px solid #6fd3e8',
  boxShadow: '0 4px 12px rgba(111, 211, 232, 0.15)',
  display: 'none',
  top: '20px',
  right: '20px',
  left: 'auto',
  fontFamily: '"Rethink Sans", sans-serif',
  '& .tooltip-header': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 8px',
    marginBottom: '4px',
    gap: '8px',
  },
  '& .tooltip-title': {
    fontSize: '14px',
    fontWeight: 400,
    color: '#262626',
    letterSpacing: '-0.4px',
    lineHeight: '1.5',
    flex: 1,
  },
  '& .tooltip-close': {
    width: '12px',
    height: '12px',
    cursor: 'pointer',
    opacity: 0.6,
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 1,
    },
  },
  '& .tooltip-content': {
    background: '#f9feff',
    border: '1px solid #c3ecf6',
    borderRadius: '8px',
    padding: '8px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  '& .tooltip-row': {
    display: 'flex',
    alignItems: 'start',
    gap: '8px',
    padding: '6px 8px',
  },
  '& .tooltip-icon': {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .tooltip-label': {
    fontSize: '14px',
    fontWeight: 400,
    color: '#262626',
    letterSpacing: '-0.4px',
    lineHeight: '1.5',
    width: '101px',
    flexShrink: 0,
  },
  '& .tooltip-value': {
    flex: 1,
    background: '#e9fbfe',
    border: '1px solid #c6f2fd',
    borderRadius: '4px',
    padding: '2px 8px',
    fontSize: '14px',
    fontWeight: 400,
    color: '#262626',
    letterSpacing: '-0.4px',
    lineHeight: '1.5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .tooltip-summary': {
    display: 'flex',
    flexDirection: 'column',
    padding: '6px 0',
  },
  '& .tooltip-summary-header': {
    display: 'flex',
    alignItems: 'start',
    gap: '8px',
    padding: '0 8px',
  },
  '& .tooltip-summary-content': {
    background: '#fafeff',
    padding: '2px 0 2px 28px',
    fontSize: '12px',
    fontWeight: 400,
    color: '#262626',
    letterSpacing: '-0.4px',
    lineHeight: '1.5',
  },
  '& a': {
    color: '#0a829d',
    textDecoration: 'none',
    fontFamily: '"Rethink Sans", sans-serif',
    fontSize: '12px',
  },
  '& a:hover': {
    textDecoration: 'underline',
  },
});

interface MindmapVisualizationProps {
  data: MindmapResponse | null;
  loading?: boolean;
  error?: string | null;
}

interface ProcessedNode extends MindmapNode {
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
  index?: number;
}

interface ProcessedLink {
  source: string | ProcessedNode;
  target: string | ProcessedNode;
  type: 'hierarchy' | 'reference';
  index?: number;
}

export default function MindmapVisualization({ data, loading, error }: MindmapVisualizationProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const selectedNodeRef = useRef<ProcessedNode | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const flattenNodes = (root: MindmapNode, ring = 0): ProcessedNode[] => {
    let result: ProcessedNode[] = [{ ...root, ring }];
    if (root.children) {
      root.children.forEach(child => {
        result = result.concat(flattenNodes(child, ring + 1));
      });
    }
    return result;
  };

  const createImplicitLinks = (node: MindmapNode, parentId: string | null = null): ProcessedLink[] => {
    const links: ProcessedLink[] = [];
    
    if (parentId) {
      links.push({
        source: parentId,
        target: node.id,
        type: 'hierarchy'
      });
    }
    
    if (node.children) {
      node.children.forEach(child => {
        links.push(...createImplicitLinks(child, node.id));
      });
    }
    
    return links;
  };

  const getNodeRadius = (d: ProcessedNode): number => {
    if (d.ring === 0) return 50;
    if (d.ring === 1) return 35 + ((d.importance || 0.5) * 15);
    return 25 + ((d.importance || 0.5) * 10);
  };

  const wrapText = (text: string, radius: number): string[] => {
    const words = text.split(/\s+/);
    const maxCharsPerLine = Math.floor(radius / 4);
    const lines: string[] = [];
    let currentLine = '';
    
    for (const word of words) {
      if ((currentLine + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
    
    return lines.slice(0, 4); // Max 3 lines
  };

  const showTooltip = (event: MouseEvent, d: ProcessedNode) => {
    if (!tooltipRef.current) return;

    const tooltip = tooltipRef.current;

    // Using Figma icon assets from MCP server
    const typeIcon = `<img src="/assets/images/SettingsCogMultiple.svg" alt="Type" style="width: 20px; height: 20px;" />`;

    const ringIcon = `<img src="/assets/images/Ring.svg" alt="Ring" style="width: 20px; height: 20px;" />`;

    const importanceIcon = `<img src="/assets/images/importance.svg" alt="Importance" style="width: 20px; height: 20px;" />`;

    const summaryIcon = `<img src="/assets/images/summary.svg" alt="Summary" style="width: 20px; height: 20px;" />`;

    const closeIcon = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 2L10 10M10 2L2 10" stroke="#262626" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`;

    // Helper to linkify plain URLs inside text
    const linkify = (text: string) => {
      const urlRegex = /(https?:\/\/[^\s)]+)|(www\.[^\s)]+)/g;
      return (text || '').replace(urlRegex, (url) => {
        const href = url.startsWith('http') ? url : `https://${url}`;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      });
    };

    // Build tooltip content with app typography
    let content = `
      <div class="tooltip-header">
        <div class="tooltip-title">${d.label}</div>
        <div class="tooltip-close" id="tooltip-close-btn">${closeIcon}</div>
      </div>
      <div class="tooltip-content">
        <div class="tooltip-row">
          <div class="tooltip-icon">${typeIcon}</div>
          <div class="tooltip-label">Type</div>
          <div class="tooltip-value">${d.type}</div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-icon">${ringIcon}</div>
          <div class="tooltip-label">Ring</div>
          <div class="tooltip-value">${d.ring}</div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-icon">${importanceIcon}</div>
          <div class="tooltip-label">Importance</div>
          <div class="tooltip-value">${((d.importance || 0.5) * 100).toFixed(0)}%</div>
        </div>
    `;

    // Add summary section if available
    if (d.meta?.summary) {
      content += `
        <div class="tooltip-summary">
          <div class="tooltip-summary-header">
            <div class="tooltip-icon">${summaryIcon}</div>
            <div class="tooltip-label">Summary</div>
          </div>
          <div class="tooltip-summary-content">${linkify(d.meta.summary)}</div>
        </div>
      `;
    }

    // Add sources (string array) if present
    if (d.meta?.sources && d.meta.sources.length > 0) {
      content += `
        <div class="tooltip-summary">
          <div class="tooltip-summary-header">
            <div class="tooltip-icon"></div>
            <div class="tooltip-label">Sources</div>
          </div>
          <div class="tooltip-summary-content">
            ${d.meta.sources.map((src, i) => {
              const href = src.startsWith('http') ? src : `https://${src}`;
              const text = src.length > 60 ? src.substring(0, 60) + '…' : src;
              return `<div><a href="${href}" target="_blank" rel="noopener noreferrer">${i + 1}. ${text}</a></div>`;
            }).join('')}
          </div>
        </div>
      `;
    }

    // Add citations (array of {label,url}) if present
    if (d.meta?.citations && d.meta.citations.length > 0) {
      content += `
        <div class="tooltip-summary">
          <div class="tooltip-summary-header">
            <div class="tooltip-icon"></div>
            <div class="tooltip-label">Sources</div>
          </div>
          <div class="tooltip-summary-content">
            ${d.meta.citations.map((c, i) => {
              const text = (c.label || c.url || '').substring(0, 60);
              return `<div><a href="${c.url}" target="_blank" rel="noopener noreferrer">${i + 1}. ${text}</a></div>`;
            }).join('')}
          </div>
        </div>
      `;
    }

    content += `</div>`;
    
    tooltip.innerHTML = content;
    tooltip.style.display = 'block';

    // Add click handler for close button
    const closeBtn = tooltip.querySelector('#tooltip-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        setSelectedNodeId(null);
        selectedNodeRef.current = null;
        hideTooltip();
      });
    }

    // Links now open in new tabs - no click interception needed
  };


  const hideTooltip = () => {
    if (!tooltipRef.current) return;
    tooltipRef.current.style.display = 'none';
  };

  const handleNodeClick = (event: MouseEvent, d: ProcessedNode) => {
    event.stopPropagation();
    // Use ref — renderMindmap closes over stale selectedNodeId (deps: [data] only).
    if (selectedNodeRef.current?.id === d.id) {
      // If clicking the same node, hide tooltip
      setSelectedNodeId(null);
      selectedNodeRef.current = null;
      hideTooltip();
    } else {
      // If clicking a different node, show its tooltip
      setSelectedNodeId(d.id);
      selectedNodeRef.current = d;
      showTooltip(event, d);
    }
  };

  const handleBackgroundClick = () => {
    // Hide tooltip when clicking on background
    setSelectedNodeId(null);
    selectedNodeRef.current = null;
    hideTooltip();
  };

  const handleNodeHover = (event: MouseEvent, d: ProcessedNode) => {
    // Always show hovered node details
    showTooltip(event, d);
  };

  const handleNodeHoverOut = () => {
    // Restore selected node details if any; otherwise hide
    if (selectedNodeRef.current) {
      const sel = selectedNodeRef.current;
      setTimeout(() => {
        showTooltip(new MouseEvent('mousemove') as unknown as MouseEvent, sel);
      }, 0);
    } else {
      hideTooltip();
    }
  };

  const renderMindmap = React.useCallback(() => {
    if (!data || !svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    const container = containerRef.current.getBoundingClientRect();
    const width = container.width;
    const height = container.height;

    // Clear previous content
    svg.selectAll('*').remove();

    svg.attr('width', width).attr('height', height).attr('viewBox', `0 0 ${width} ${height}`);

    // Create gradients for styling
    const defs = svg.append('defs');
    const centerGradient = defs.append('radialGradient')
      .attr('id', 'centerGradient')
      .attr('cx', '50%')
      .attr('cy', '50%')
      .attr('r', '50%');

    centerGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#ffffff')
      .attr('stop-opacity', 1);

    centerGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#ffffff')
      .attr('stop-opacity', 1);

    // Linear gradient for main node stroke
    const mainRingGradient = defs.append('linearGradient')
      .attr('id', 'mainRingGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '0%');
    mainRingGradient.append('stop').attr('offset', '0%').attr('stop-color', '#0A829D');
    mainRingGradient.append('stop').attr('offset', '25%').attr('stop-color', '#35D7E4');
    mainRingGradient.append('stop').attr('offset', '50%').attr('stop-color', '#0A829D');
    mainRingGradient.append('stop').attr('offset', '75%').attr('stop-color', '#35D7E4');
    mainRingGradient.append('stop').attr('offset', '100%').attr('stop-color', '#0A829D');

    // Gradient palette for child nodes (used across all non-center nodes)
    const childGradients = [
      { id: 'nodeGrad0', colors: ['#D13438', '#E7A3A7', '#D13438', '#FDE7E9', '#A4262C'] },
      { id: 'nodeGrad1', colors: ['#6B69D6', '#B8B6E9', '#6B69D6', '#EBEBF9', '#6B69D6'] },
      { id: 'nodeGrad2', colors: ['#107C10', '#8DC88D', '#107C10', '#DFF6DD', '#107C10'] },
      { id: 'nodeGrad3', colors: ['#0178D4', '#A4C7E8', '#0178D4', '#E6F2FB', '#004E8C'] },
      { id: 'nodeGrad4', colors: ['#C19B00', '#EDD27A', '#C19B00', '#FFF8D6', '#8E6B00'] },
      { id: 'nodeGrad5', colors: ['#B4009E', '#E3A9DA', '#B4009E', '#FAE9F6', '#7A0067'] },
      { id: 'nodeGrad6', colors: ['#FF8C00', '#FFC27A', '#FF8C00', '#FFF0DA', '#B25F00'] },
      { id: 'nodeGrad7', colors: ['#0078D4', '#90C3F0', '#0078D4', '#E6F4FF', '#004E8C'] },
      { id: 'nodeGrad8', colors: ['#8E562E', '#D6B69D', '#8E562E', '#F6EAE1', '#603813'] },
      { id: 'nodeGrad9', colors: ['#00A3A3', '#76DADA', '#00A3A3', '#E3FAFA', '#006B6B'] },
      { id: 'nodeGrad10', colors: ['#FF4F81', '#FFB3C8', '#FF4F81', '#FFE8F0', '#C02B56'] },
      { id: 'nodeGrad11', colors: ['#5C2D91', '#B9A2D6', '#5C2D91', '#F0EAF8', '#3C1D5C'] },
      { id: 'nodeGrad12', colors: ['#2D7D46', '#8BC1A2', '#2D7D46', '#E8F5EE', '#1B4F2A'] },
      { id: 'nodeGrad13', colors: ['#B146C2', '#E1B3EA', '#B146C2', '#F8EBFC', '#6E2C78'] },
      { id: 'nodeGrad14', colors: ['#0063B1', '#9AC2E6', '#0063B1', '#E6F0FA', '#003B6F'] },
      { id: 'nodeGrad15', colors: ['#8C8C00', '#D8D88F', '#8C8C00', '#F9F9E5', '#5C5C00'] },
    ];
    childGradients.forEach(gd => {
      const lg = defs.append('linearGradient')
        .attr('id', gd.id)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '0%');
      lg.append('stop').attr('offset', '0%').attr('stop-color', gd.colors[0]);
      lg.append('stop').attr('offset', '25%').attr('stop-color', gd.colors[1]);
      lg.append('stop').attr('offset', '50%').attr('stop-color', gd.colors[2]);
      lg.append('stop').attr('offset', '75%').attr('stop-color', gd.colors[3]);
      lg.append('stop').attr('offset', '100%').attr('stop-color', gd.colors[4]);
    });

    // Create main group
    const g = svg.append('g');

    // Flatten nodes and create links
    const nodes = flattenNodes(data.root);
    const links = [...(data.links || []), ...createImplicitLinks(data.root)];

    const cx = width / 2;
    const cy = height / 2;

    // Fix center node position
    const centerNode = nodes.find(n => n.ring === 0);
    if (centerNode) {
      centerNode.fx = cx;
      centerNode.fy = cy;
    }

    // Provide a non-overlapping initial layout (radial per ring) to avoid overlap on load
    const byRing: Record<number, ProcessedNode[]> = {};
    nodes.forEach(n => {
      const r = (n.ring || 0) as number;
      if (!byRing[r]) byRing[r] = [];
      byRing[r].push(n as ProcessedNode);
    });
    Object.entries(byRing).forEach(([ringStr, ringNodes]) => {
      const ring = Number(ringStr);
      if (ring === 0) {
        // Center already fixed at cx, cy
        (ringNodes[0] as ProcessedNode).x = cx;
        (ringNodes[0] as ProcessedNode).y = cy;
        return;
      }
      const count = ringNodes.length;
      const ringRadius = ring === 1 ? 110 : 140 + (ring - 1) * 110; // tighter first ring only
      ringNodes.forEach((n, i) => {
        const angle = (i / count) * Math.PI * 2;
        n.x = cx + ringRadius * Math.cos(angle);
        n.y = cy + ringRadius * Math.sin(angle);
      });
    });

    // Setup force simulation
    const simulation = d3.forceSimulation(nodes as ProcessedNode[])
      .force('link', d3.forceLink(links).id(d => (d as ProcessedNode).id).distance(d => {
        const src = nodes.find(n => n.id === (typeof d.source === 'string' ? d.source : (d.source as ProcessedNode).id));
        const tgt = nodes.find(n => n.id === (typeof d.target === 'string' ? d.target : (d.target as ProcessedNode).id));
        const isCenterLink = (src?.ring === 0) || (tgt?.ring === 0);
        if (isCenterLink) {
          const baseCenter = d.type === 'reference' ? 80 : 70;
          return baseCenter + (Math.max(src?.ring || 0, tgt?.ring || 0) * 20);
        }
        const base = d.type === 'reference' ? 100 : 90; // reduce child-to-child spacing
        return base + (Math.max(src?.ring || 0, tgt?.ring || 0) * 18);
      }).strength(0.8))
      .force('charge', d3.forceManyBody().strength(d => (d as ProcessedNode).ring === 0 ? -1400 : -380))
      .force('center', d3.forceCenter(cx, cy))
      .force('collision', d3.forceCollide().radius(d => getNodeRadius(d as ProcessedNode) + 8).iterations(2));

    // Build links
    const linkGroup = g.append('g').attr('class', 'links');
    const link = linkGroup.selectAll('.link')
      .data(links)
      .enter().append('path')
      .attr('class', d => `link ${d.type || 'hierarchy'}`)
      .style('fill', d => d.type === 'reference' ? '#065B70' : '#065B70')
      .style('opacity', '0');

    // Assign gradient ids: first-level children get unique gradients, and all their descendants inherit the same gradient
    const gradientByNodeId: Record<string, string> = {};
    const assignGradient = (node: MindmapNode, gradientId: string) => {
      gradientByNodeId[node.id] = gradientId;
      if (node.children) node.children.forEach(ch => assignGradient(ch, gradientId));
    };
    const rootChildren = data.root.children || [];
    rootChildren.forEach((child, idx) => {
      const gradId = `nodeGrad${idx % childGradients.length}`;
      assignGradient(child, gradId);
    });
    nodes.forEach(n => {
      const gid = gradientByNodeId[n.id];
      // Every non-center node is under a root child → always has a gradient id.
      if (gid) (n as any).strokeGradientId = gid;
      else if ((n.ring || 0) > 0) (n as any).strokeGradientId = 'nodeGrad0';
    });

    // Build nodes
    const nodeGroup = g.append('g').attr('class', 'nodes');
    const node = nodeGroup.selectAll('.node')
      .data(nodes)
      .enter().append('g')
      .attr('class', d => `node ${d.ring === 0 ? 'center-node' : ''}`)
      .style('cursor', 'pointer')
      .call(d3.drag<SVGGElement, ProcessedNode>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended))
      .on('dblclick', dblclicked);

    // Add circles
    node.append('circle')
      .attr('r', 0)
      .style('fill', '#ffffff')
      .style('opacity', 1)
      .style('stroke', function(d) {
        if (d.ring === 0) return 'url(#mainRingGradient)';
        return `url(#${(d as any).strokeGradientId || 'nodeGrad0'})`;
      })
      .style('stroke-width', d => (d.ring === 0 ? '1px' : '1px'))
      .style('filter', 'none');

    // Add curved left-side arc for child nodes
    // In your node.each(function(d)) section, replace the arc styling:
node.each(function(d) {
  const circle = d3.select(this).select('circle');
  const proc = d as ProcessedNode;
  
  if (proc.ring > 0) {
    const r = getNodeRadius(proc);
    const c = 2 * Math.PI * r;
    const dash = c * 0.35; // visible arc length (~35% of circumference)
    const gap = c - dash; // remaining circumference as gap
    
    // Position arc vertically centered on the right side (90° to 270°)
    const offset = c * 0.65; // 90° = 1/4 of circumference
    
    circle
      .style('stroke-linecap', 'round') // This makes the ends rounded and thicker
      .style('stroke-dasharray', `${dash} ${gap}`)
      .style('stroke-dashoffset', `${offset}`)
      .style('stroke-width', d => {
        // Make stroke thicker based on ring and importance
        if (selectedNodeId === proc.id) return '4px'; // Even thicker when selected
        if (proc.ring === 1) return '3px'; // Thicker for first ring
        if (proc.ring === 2) return '2px'; // Medium for second ring
        return '1px'; // Default for other rings
      })
      .style('filter', 'drop-shadow(0px 1px 16.6px rgba(6, 91, 112, 0.2))');
  } else {
    // Center node - make it thicker too
    circle
      .style('stroke-width', selectedNodeId === proc.id ? '8px' : '6px')
      .style('stroke-linecap', 'round')
      .style('filter', 'drop-shadow(0px 1px 16.6px rgba(6, 91, 112, 0.2))');
  }
});

    // Add labels
    node.each(function(d) {
      const group = d3.select(this);
      const radius = getNodeRadius(d);
      const lines = wrapText(d.label, radius);
      const fontSize = d.ring === 0 ? 14 : (d.ring === 1 ? 11 : 9);
      const lineHeight = fontSize * 1.1;
      const startY = -(lines.length * lineHeight) / 2 + (lineHeight / 2);

      lines.forEach((line, i) => {
        group.append('text')
          .attr('class', 'node-text-line')
          .attr('y', startY + (i * lineHeight))
          .style('font-size', `${fontSize}px`)
          .style('font-family', '"Rethink Sans", sans-serif')
          .style('text-anchor', 'middle')
          .style('dominant-baseline', 'central')
          .style('pointer-events', 'none')
          .style('font-weight', d.ring === 0 ? '700' : '600')
          .style('fill', d.ring === 0 ? '#033843' : '#033843')
          .style('text-shadow', 'none')
          .style('opacity', 0)
          .text(line);
      });
    });

    // Set initial transform so nodes are placed at their initial non-overlapping positions before first tick
    node.attr('transform', d => `translate(${(d as ProcessedNode).x || cx},${(d as ProcessedNode).y || cy})`);

    // Click and hover handlers
    node
      .on('click', handleNodeClick)
      .on('mouseover', handleNodeHover)
      .on('mouseout', handleNodeHoverOut);

    // Background click handler
    svg.on('click', (event) => {
      // Only hide tooltip if clicking on the background (not on nodes)
      if (event.target === svg.node()) {
        handleBackgroundClick();
      }
    });

    // Zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    // Start simulation immediately without animation
    simulation.on('tick', () => {
      const effectiveRadius = (n: ProcessedNode) => {
        const base = getNodeRadius(n);
        const stroke = (n.ring === 0 ? 4 : 3);
        const pad = 2; // small visual gap to avoid overlapping the ring
        return Math.max(0, base - stroke / 2 - pad);
      };
      link
        .attr('d', d => {
          const s = d.source as ProcessedNode;
          const t = d.target as ProcessedNode;
          const sx = s.x!;
          const sy = s.y!;
          const tx = t.x!;
          const ty = t.y!;
          const dx = tx - sx;
          const dy = ty - sy;
          const dist = Math.hypot(dx, dy);
          if (dist < 1e-6) return '';
          const sr = effectiveRadius(s);
          const sy2 = sy + (dy / dist) * sr;
          const sx2 = sx + (dx / dist) * sr;
          const tr = effectiveRadius(t);
          const tx2 = tx - (dx / dist) * tr;
          const ty2 = ty - (dy / dist) * tr;

          // widths: thinner overall per request (tapered)
          const w1 = 1.2; // px near parent
          const w2 = 0.2; // px near child
          // perpendicular unit
          const nx = -dy / dist;
          const ny = dx / dist;

          const p1x = sx2 + nx * w1;
          const p1y = sy2 + ny * w1;
          const p2x = tx2 + nx * w2;
          const p2y = ty2 + ny * w2;
          const p3x = tx2 - nx * w2;
          const p3y = ty2 - ny * w2;
          const p4x = sx2 - nx * w1;
          const p4y = sy2 - ny * w1;

          return `M ${p1x},${p1y} L ${p2x},${p2y} L ${p3x},${p3y} L ${p4x},${p4y} Z`;
        });

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Show elements immediately
    link
      .style('stroke-width', '3px')
      .style('opacity', 1);

    node.select('circle')
      .attr('r', getNodeRadius)
      .style('opacity', 1);

    node.selectAll('text')
      .style('opacity', 1);

    // Add zoom behavior
    svg.call(zoom);

    function dragstarted(event: d3.D3DragEvent<SVGGElement, ProcessedNode, ProcessedNode>, d: ProcessedNode) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: d3.D3DragEvent<SVGGElement, ProcessedNode, ProcessedNode>, d: ProcessedNode) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: d3.D3DragEvent<SVGGElement, ProcessedNode, ProcessedNode>, d: ProcessedNode) {
      if (!event.active) simulation.alphaTarget(0);
      // Keep node fixed where it was dropped
      d.fx = event.x;
      d.fy = event.y;
    }

    function dblclicked(event: MouseEvent, d: ProcessedNode) {
      // Release node back to simulation
      d.fx = null;
      d.fy = null;
      simulation.alpha(0.3).restart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!data || !svgRef.current || loading) return;

    renderMindmap();
  }, [data, loading, renderMindmap]);

  // Update node styling when selectedNodeId changes
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const node = svg.selectAll('.node circle');
    
    node
      .style('stroke', d => {
        const procNode = d as ProcessedNode;
        if (selectedNodeId === procNode.id) return '#fbbf24';
        if (procNode.ring === 0) return 'url(#mainRingGradient)';
        return `url(#${(procNode as any).strokeGradientId || 'nodeGrad0'})`;
      })
      .style('stroke-width', d => selectedNodeId === (d as ProcessedNode).id ? '5px' : ((d as ProcessedNode).ring === 0 ? '4px' : '3px'))
      .style('filter', 'none');
  }, [selectedNodeId]);

  if (loading) {
    return (
      <MindmapContainer>
        <LoadingContainer>
          <CircularProgress size={40} sx={{ color: "#0a829d" }} />
          <Typography variant="body1">
            Generating compliance mindmap...
          </Typography>
        </LoadingContainer>
      </MindmapContainer>
    );
  }

  if (error) {
    return (
      <MindmapContainer>
        <LoadingContainer>
          <Typography variant="body1" color="error">
            Error: {error}
          </Typography>
        </LoadingContainer>
      </MindmapContainer>
    );
  }

  if (!data) {
    return (
      <MindmapContainer>
        <LoadingContainer>
          <Typography variant="body1" color="textSecondary">
            No mindmap data available
          </Typography>
        </LoadingContainer>
      </MindmapContainer>
    );
  }

  return (
    <MindmapContainer ref={containerRef}>
      <Box sx={{ 
        position: 'absolute', 
        top: 8, 
        left: 8, 
        zIndex: 10,
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        💡 Click on nodes to view details
      </Box>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      <TooltipContainer ref={tooltipRef} />
      
    </MindmapContainer>
  );
}